// Pure helpers for the Marketing page (unit-tested).

export const toEur = (usd, rate) => (Number(usd) || 0) * (Number(rate) || 0);

export const formatEur = (eur) => {
  const n = Number(eur) || 0;
  if (n > 0 && n < 0.005) return '<€0.01';
  return `€${n.toFixed(2)}`;
};

export const itemSpentUsd = (cost) => (cost?.draftUsd || 0) + (cost?.reviewUsd || 0);

const OPEN = ['pending_approval', 'flagged'];
export const OUTBOX_STATUSES = ['approved', 'sending', 'failed'];
const inOutbox = (i) => i.channel === 'outreach' && OUTBOX_STATUSES.includes(i.status);

export const isOpen = (i) => OPEN.includes(i.status);

export const filterByTab = (items, tab) => {
  if (tab === 'flagged') return items.filter((i) => i.status === 'flagged');
  if (tab === 'outbox') return items.filter(inOutbox);
  if (tab === 'history') return items.filter((i) => !OPEN.includes(i.status) && !inOutbox(i));
  return items.filter((i) => (i.channel || 'content') === tab && i.status === 'pending_approval');
};

export const outreachStateLabel = (s) => (s.outreachPaused
  ? `Outreach paused${s.outreachPauseReason ? `: ${s.outreachPauseReason}` : ''}`
  : `Outreach on (${s.dailyOutreachLimit}/day)`);

// Mirrors DEFAULT_SETTINGS in functions/marketing/config.js.
export const SETTINGS_DEFAULTS = {
  capEur: 50, usdToEur: 0.92, mode: 'dry_run', paused: false,
  dailyAnomalyEur: 5, contentPerDay: 3, backlogSoftLimit: 30, backlogHardLimit: 45,
  creditsRemainingUsd: null,
  dailyOutreachLimit: 5, outreachPaused: false, outreachPauseReason: null,
  alertEmails: ['umit.kavak@dierend.com'], dryRunRecipient: 'umit.kavak@dierend.com',
  outreachAreas: ['Merelbeke-Melle', 'Gent'],
};

export const linesToList = (text) => String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);
export const listToLines = (list) => (Array.isArray(list) ? list.join('\n') : String(list || ''));

export const OUTREACH_STATUS_LABEL = {
  approved: 'Queued to send', sending: 'Sending', sent: 'Sent', failed: 'Send failed', skipped: 'Skipped',
};

export const spendRatio = (spentEur, capEur) => (capEur > 0 ? Math.min(1, spentEur / capEur) : 1);

const MONTH = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit' });
export const brusselsMonthKey = (date = new Date()) => MONTH.format(date);

export const BANNERS = {
  budget_reached: 'Monthly budget reached — no new drafts until the cap is raised or the month ends.',
  backlog_full: 'Too many drafts are waiting — approve or skip some so the agent can continue.',
  daily_anomaly: 'Today\'s spend crossed the daily safety limit, so the agent paused itself. Check the costs, then resume.',
  collect_failed: 'A batch of drafts could not be collected after 3 tries. Its estimated cost was booked to this month\'s spend. Check the Cloud Functions logs.',
  stuck_submission: 'A daily run has been stuck while sending its batch to Claude for over 30 minutes. Check the Cloud Functions logs.',
  run_failed: 'The last run could not send its batch to Claude (for example a revoked API key). Check the Cloud Functions logs.',
  outreach_paused: 'Outreach paused itself because of bounces or a spam complaint — see the reason in the top bar. Fix the cause before resuming.',
  reservation_failed: 'Drafts were requested but some businesses could not be reserved for them. A business may get a duplicate draft — skip any duplicate. Check the Cloud Functions logs.',
};

export const SKIP_REASONS = ['Wrong tone', 'Inaccurate', 'Not relevant', 'Repetitive', 'Other'];

// `r.failed` is only meaningful when the collector actually returned an
// array (it always does on success, but a defensive guard here keeps a
// malformed/partial response from rendering "failed 0" or "failed undefined").
export const collectResultMessage = (r) => (
  `Collected ${r.collected.length}, waiting ${r.waiting.length}`
  + (Array.isArray(r.failed) ? `, failed ${r.failed?.length}` : '')
);

export const RUN_NOW_CONFIRM_MESSAGE = 'Run the agent now? This makes real Claude calls (up to about €0.70).';
